import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { cleanupTooltipsOnNavigation } from './utils/tooltipCleanup';
import { canAccessPath, firstAccessiblePath } from './utils/featureAccess';
import { isSuperAdminUser } from './utils/userRoles';
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import RegistrationPage from './pages/RegistrationPage';
import UserManagementPage from './pages/UserManagementPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import LandingPage from './pages/LandingPage';
import CorporateRouteLayout from './layouts/CorporateRouteLayout';
import CorporateCommandCentre from './pages/CorporateCommandCentre';
import CorporatePage from './pages/CorporatePage';
import PolicyMakerRouteLayout from './layouts/PolicyMakerRouteLayout';
import PolicyMakersRouteLayout from './layouts/PolicyMakersRouteLayout';
import PolicyMakerCommandCentre from './pages/PolicyMakerCommandCentre';
import DataPage from './pages/DataPage';
import CriticalMineralsDashboard from './pages/CriticalMineralsDashboard';
import OverviewPage from './pages/OverviewPage';
import SupplyMarketsPage from './pages/SupplyMarketsPage';
import PartnersRiskPage from './pages/PartnersRiskPage';
import PolicyIntelligencePage from './pages/PolicyIntelligencePage';
import DataUpload from './pages/DataUpload';
import PartnerRiskTable from './pages/PartnerRiskTable';
import CriticalMineralPoliciesTable from './pages/CriticalMineralPoliciesTable';
import TradePolicyTracker from './pages/TradePolicyTracker';
import HSCodesPage from './pages/HSCodesPage';
import Methodology from './pages/Methodology';
import GlobalCompetitiveness from './pages/GlobalCompetitiveness';
import PredictiveAlerts from './pages/PredictiveAlerts';
import CrisisSimulator from './pages/CrisisSimulator';
import EVDashboard from './pages/EVDashboard';
import EVCrisisSimulator from './pages/EVCrisisSimulator';
import ExecutiveSummaryPage from './pages/ExecutiveSummaryPage';
import CountriesSituationData from './pages/CountriesSituationData';
// import GeoeconomicAIDashboard from './pages/GeoeconomicAIDashboard';
// import CompanyRiskIntelligencePage from './pages/CompanyRiskIntelligencePage';
import GlobalResourceSentinel from './components/GlobalResourceSentinel';
import SearchPage from './pages/SearchPage';
import EventTracker from './pages/EventTracker';
import IntelligenceBriefing from './pages/IntelligenceBriefing';
// import PolicyMarketBarometer from './pages/PolicyMarketBarometer';
// import PestleStats from './pages/PestleStats';
// import AnalyzedArticlesTable from './pages/AnalyzedArticlesTable';
// import DependencyRatioTable from './pages/DependencyRatioTable';
// import CriticalMineralsNavigation from './components/CriticalMineralsNavigation';
// import Dashboard from './pages/Dashboard';
// import DashboardEnhanced from './pages/DashboardEnhanced';
// import DashboardSettings from './pages/DashboardSettings';
// import ArticleAnalyzer from './pages/ArticleAnalyzer';
// import BatchAnalyzer from './pages/BatchAnalyzer';
// import ArticleHistory from './pages/ArticleHistory';
// import Analytics from './pages/Analytics';
// import SignalsDashboard from './pages/SignalsDashboard';
// import EarlyWarning from './pages/EarlyWarningRealTime';
// import CriticalMineralsOutlook from './pages/CriticalMineralsOutlook';
// import ActionInsights from './pages/ActionInsights';
// import StrategicExposure from './pages/StrategicExposure';
// import MarketIntelligence from './pages/MarketIntelligence';
// import Foresight from './pages/Foresight';
// import PartnerRiskDashboard from './components/PartnerRiskDashboard';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const features = user?.subscription_plan_features || [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (location.pathname === '/admin/users' && !isSuperAdminUser(user)) {
    return <Navigate to={firstAccessiblePath(features, user)} replace />;
  }

  if (!canAccessPath(location.pathname, features, user)) {
    return <Navigate to={firstAccessiblePath(features, user)} replace />;
  }

  return children;
};

function AppContent() {
  // Cleanup tooltips on navigation and page changes
  useEffect(() => {
    cleanupTooltipsOnNavigation();
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegistrationPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <LandingPage />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/corporate"
          element={
            <ProtectedRoute>
              <CorporateRouteLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<CorporatePage />} />
          <Route path=":screenSlug" element={<CorporateCommandCentre />} />
        </Route>
        <Route
          path="/policy-maker"
          element={
            <ProtectedRoute>
              <PolicyMakerRouteLayout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/policy-makers"
          element={
            <ProtectedRoute>
              <PolicyMakersRouteLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="alerts" replace />} />
          <Route path="import-bill" element={<Navigate to="import-exposure" replace />} />
          <Route path="china-exposure" element={<Navigate to="domestic-capability" replace />} />
          <Route path="mou-tracker" element={<Navigate to="trade-agreements" replace />} />
          <Route path="chokepoints" element={<Navigate to="ports-logistics" replace />} />
          <Route path="peer-nations" element={<Navigate to="geopolitical-risk" replace />} />
          <Route path=":screenSlug" element={<PolicyMakerCommandCentre />} />
        </Route>
        <Route
          path="/data"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <DataPage />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <SearchPage />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/critical-minerals-dashboard"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <CriticalMineralsDashboard />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/overview"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <OverviewPage />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/supply-markets"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <SupplyMarketsPage />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/partners-risk"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <PartnersRiskPage />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/policy-intelligence"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <PolicyIntelligencePage />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        { <Route
          path="/methodology"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <Methodology />
                </main>
              </div>
            </ProtectedRoute>
          }
        />}
        <Route
          path="/trade-policy-tracker"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <TradePolicyTracker />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/global-competitiveness"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <GlobalCompetitiveness />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        {<Route
          path="/predictive-alerts"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <PredictiveAlerts />
                </main>
              </div>
            </ProtectedRoute>
          }
        />}
        {<Route
          path="/crisis-simulator"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <CrisisSimulator />
                </main>
              </div>
            </ProtectedRoute>
          }
        />}
        <Route
          path="/ev-dashboard"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <EVDashboard />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ev_crisis_simulator"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <EVCrisisSimulator />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/critical-mineral-policies"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <CriticalMineralPoliciesTable />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/partner-risk-table"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <PartnerRiskTable />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/data-upload"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <DataUpload />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hs-codes"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <HSCodesPage />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/executive-summary"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <ExecutiveSummaryPage />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        {/* <Route
          path="/geoeconomic-ai"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <GeoeconomicAIDashboard />
                </main>
              </div>
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/event-tracker"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <EventTracker />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/intelligence-briefing"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <IntelligenceBriefing />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        {/* <Route
          path="/stratosphere-risk-intelligence"
          element={<Navigate to="/company-risk-intelligence" replace />}
        />
        <Route
          path="/company-risk-intelligence"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <CompanyRiskIntelligencePage />
                </main>
              </div>
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/global-resource-sentinel"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <GlobalResourceSentinel />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/countries-situation-data"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <CountriesSituationData />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <UserManagementPage />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/account-settings"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200 w-full">
                <Navbar />
                <main className="critical-minerals-dashboard-main w-full">
                  <AccountSettingsPage />
                </main>
              </div>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 